/**
 * Medications API
 * All 8 endpoints for the medication reconciliation system.
 */

import { onApiMutation } from '@/utils/apiMutationListener';
import { apiClient } from './config';

// ─── Default Catalog (global, not per-patient) ────────────────────────────────

// Returns { supplements: [{name,...}], dispensed: [{name,...}] }
export const fetchDefaultItemsApi = () =>
  apiClient.get('/api/patient/order/default-items');

// GET /getAllSupplements, shared by the catalog refresh in DataContext and the
// medications defaults below, which used to ask for it at the same time.
// A request already on its way is always joined. Without `force`, an answer received
// in the last SUPPLEMENTS_REUSE_MS is reused: DataContext refreshes the catalog that
// often, so it is never older than what the page already shows. Any write this tab
// sends (it may have changed the catalog) drops the answer, and the next call asks
// again instead of joining a request sent before the write.
export const SUPPLEMENTS_REUSE_MS = 50 * 1000;
let supplementsInFlight = null;
let lastSupplements = null; // { response, at }
let listeningForWrites = false;

export const fetchAllSupplementsShared = ({ force = false } = {}) => {
  if (!listeningForWrites) {
    listeningForWrites = true;
    onApiMutation(() => {
      supplementsInFlight = null;
      lastSupplements = null;
    });
  }
  if (supplementsInFlight) return supplementsInFlight;
  if (!force && lastSupplements && Date.now() - lastSupplements.at <= SUPPLEMENTS_REUSE_MS) {
    return Promise.resolve(lastSupplements.response);
  }
  let shared;
  const request = apiClient.get('/getAllSupplements').then((response) => {
    // Not kept when a write was sent while it was on its way
    if (supplementsInFlight === shared) lastSupplements = { response, at: Date.now() };
    return response;
  });
  shared = request.finally(() => {
    if (supplementsInFlight === shared) supplementsInFlight = null;
  });
  supplementsInFlight = shared;
  return shared;
};

// Returns [{ supplement_name, dosages, frequencies }]
export const fetchAllSupplementsApi = () => fetchAllSupplementsShared();

// ─── Fetch Sources ─────────────────────────────────────────────────────────────

export const fetchEMRSupplementsApi = (patientId) =>
  apiClient.get(`/${patientId}/get-emr-supplements`);

export const fetchEMRDispensedApi = (patientId) =>
  apiClient.get(`/${patientId}/get-emr-dispensed`);

export const fetchActiveDispensedApi = (customerId) =>
  apiClient.get(`/active-dispensed-items/${customerId}`);

// Returns: { orderSupplements: [], patientPlanSupplements: { recommended: [], historical: [] } }
export const fetchPatientSupplementsApi = (customerId) =>
  apiClient.get(`/patient/${customerId}/supplements`);

export const fetchCustomMedicationsApi = (patientId) =>
  apiClient.get(`/emr/get-medications/${patientId}`);

// ─── Mutations ─────────────────────────────────────────────────────────────────

// medication: { supplementName, frequency, isCustom: true }
export const addMedicationApi = (patientId, medication) =>
  apiClient.post('/emr/add-medication', { patientId, medication });

// itemData: { name, checked, status, frequency, selectedField, source, startDate?, endDate? }
export const patchEMRSupplementApi = (patientId, itemData) =>
  apiClient.patch(`/${patientId}/update-emr-supplement`, itemData);

export const patchEMRDispensedApi = (patientId, itemData) =>
  apiClient.patch(`/${patientId}/update-emr-dispensed`, itemData);
